import { createStyles, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-quill/dist/quill.snow.css';
import { useDispatch } from 'react-redux';
import { getTranslatorTextBlock, putTranslatorTranslations, TranslatorTextBlock } from 'src/api/translator';
import ModalShell from 'src/components/account/organization/dashboard/modal/ModalShell';
import { ModalStackProvider } from 'src/components/account/organization/dashboard/modal/ModalStackContext';
import SafeReactQuill from 'src/components/elements/form-fields/SafeReactQuill';
import { sanitizeTextBlockHtml } from 'src/components/elements/formats/SafeHTMLRender';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';

const SNACK_KEY = 'account-translations-editor';

/** Bold, underline, paragraphs and lists — what a marketing text block may carry (R34-3). */
const RICH_TEXT_MODULES = {
    toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']],
};
const RICH_TEXT_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet'];
// website labels are plain text: the formatting editor only where the block already carries markup
const RICH_MARKUP = /<\/?(p|br|strong|b|em|i|u|ol|ul|li)\b/i;
const EMPTY_RICH_TEXT = '<p><br></p>';

const useStyles = makeStyles(() =>
    createStyles({
        rowKey: { fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontSize: 13, color: '#1c1c19' },
        section: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 },
        sectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
        label: { fontSize: 12, fontWeight: 700, color: '#374151' },
        select: {
            fontFamily: 'inherit',
            fontSize: 14,
            padding: '6px 10px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            background: '#fff',
        },
        pills: { display: 'flex', flexWrap: 'wrap', gap: 6 },
        pill: {
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 700,
            padding: '5px 12px',
            borderRadius: 999,
            border: '1px solid #d1d5db',
            background: '#fff',
            color: '#374151',
            cursor: 'pointer',
            '&.is-active': { borderColor: '#93000b', background: '#fde2e0', color: '#93000b' },
        },
        changedMarker: { fontWeight: 400, marginLeft: 6 },
        readOnlyValue: {
            padding: '10px 12px',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#f9fafb',
            fontSize: 14,
            color: '#374151',
            minHeight: 40,
            whiteSpace: 'pre-wrap',
        },
        textarea: {
            width: '100%',
            boxSizing: 'border-box',
            minHeight: 96,
            fontFamily: 'inherit',
            fontSize: 14,
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            resize: 'vertical',
        },
        muted: { color: '#9ca3af', fontStyle: 'italic', margin: 0 },
        actions: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 },
        ghostBtn: {
            fontFamily: 'inherit',
            fontSize: 14,
            padding: '9px 16px',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            background: '#fff',
            cursor: 'pointer',
        },
        primaryBtn: {
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 600,
            padding: '9px 16px',
            borderRadius: 8,
            border: 'none',
            background: '#93000b',
            color: '#fff',
            cursor: 'pointer',
            '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
        },
    })
);

export interface TextBlockEditModalProps {
    blockKey: string;
    /** The row the list already holds; without it the block is read by its key. */
    block?: TranslatorTextBlock;
    onClose: () => void;
    onSaved?: () => void;
}

const normalizeDraft = (value: string): string => (value === EMPTY_RICH_TEXT ? '' : value);

function firstOf(options: string[], preferred: string[]): string {
    return preferred.find(iso2 => options.includes(iso2)) ?? options[0] ?? '';
}

/**
 * R49-4 "Textblock bearbeiten" of Mein Bereich › Übersetzungen (also opened by the Übersetzermodus):
 * on top the sample text in a language the translator may view (website language, else EN, else
 * DE first), below the language he edits — switchable when he may edit several; the drafts of
 * every edit language survive the switch and one "Speichern" writes all changed ones.
 */
const TextBlockEditModal: React.FC<TextBlockEditModalProps> = ({ blockKey, block: initialBlock, onClose, onSaved }) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const websiteLanguage = (i18n.language || 'de').split('-')[0].toLowerCase();

    const [block, setBlock] = useState<TranslatorTextBlock | null>(initialBlock ?? null);
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [displayLanguage, setDisplayLanguage] = useState('');
    const [editLanguage, setEditLanguage] = useState('');
    const [saving, setSaving] = useState(false);
    const editorRef = useRef<HTMLDivElement>(null);
    // the parent passes a fresh close callback per render; the key read must not re-run for it
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const notify = useCallback(
        (message: string, variant: 'success' | 'error') =>
            dispatch(enqueueSnackbarAction({ message, options: { key: SNACK_KEY + Date.now(), variant } })),
        [dispatch]
    );

    useEffect(() => {
        if (initialBlock) return undefined;
        let active = true;
        getTranslatorTextBlock(blockKey).then(result => {
            if (!active) return;
            if (result.response) setBlock(result.response.data);
            else {
                notify(t('account.translations.load_failed', { message: result.error?.message ?? '' }), 'error');
                onCloseRef.current();
            }
        });
        return () => {
            active = false;
        };
    }, [blockKey, initialBlock, notify, t]);

    const originals = useMemo(
        () => Object.fromEntries((block?.translations ?? []).map(entry => [entry.languageIso2, entry.value ?? ''])),
        [block]
    );
    const editOptions = useMemo(
        () => (block?.translations ?? []).filter(entry => entry.editable).map(entry => entry.languageIso2),
        [block]
    );
    const viewOptions = useMemo(() => {
        const all = (block?.translations ?? []).map(entry => entry.languageIso2);
        const viewOnly = all.filter(iso2 => !editOptions.includes(iso2));
        return viewOnly.length > 0 ? viewOnly : all;
    }, [block, editOptions]);
    const richText = useMemo(
        () => (block?.translations ?? []).some(entry => !!entry.value && RICH_MARKUP.test(entry.value)),
        [block]
    );

    useEffect(() => {
        if (!block) return;
        setDrafts(originals);
        setDisplayLanguage(firstOf(viewOptions, [websiteLanguage, 'en', 'de']));
        setEditLanguage(firstOf(editOptions, [websiteLanguage]));
        // the language choice follows the block, not later flag switches
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [block]);

    useEffect(() => {
        const field = editorRef.current?.querySelector<HTMLElement>('textarea, [contenteditable="true"]');
        if (!field) return;
        field.focus({ preventScroll: true });
        field.scrollIntoView({ block: 'center' });
    }, [editLanguage]);

    const languageLabel = (iso2: string) => {
        const name = t(`languages:${iso2}`, { defaultValue: '' });
        return name && name !== iso2 ? `${iso2.toUpperCase()} · ${name}` : iso2.toUpperCase();
    };
    const isChanged = (iso2: string) => normalizeDraft(drafts[iso2] ?? '') !== (originals[iso2] ?? '');
    const changedLanguages = editOptions.filter(isChanged);

    const save = async () => {
        if (!block || changedLanguages.length === 0) return;
        setSaving(true);
        const result = await putTranslatorTranslations(
            block.key,
            Object.fromEntries(changedLanguages.map(iso2 => [iso2, normalizeDraft(drafts[iso2] ?? '')]))
        );
        setSaving(false);
        if (!result.response) {
            notify(t('account.translations.save_failed', { message: result.error?.message ?? '' }), 'error');
            return;
        }
        notify(t('account.translations.saved'), 'success');
        onSaved?.();
        onClose();
    };

    const displayValue = originals[displayLanguage] ?? '';
    const setDraft = (value: string) => setDrafts(current => ({ ...current, [editLanguage]: value }));

    return (
        <ModalStackProvider>
            <ModalShell
                id="translations-editor"
                open
                onClose={onClose}
                eyebrow={blockKey}
                title={t('account.translations.editor_title')}>
                <div data-testid="translations-editor" data-translator-mode-ignore="">
                    <span className={classes.rowKey} data-testid="translations-editor-key">
                        {blockKey}
                    </span>
                    {!block && <p className={classes.muted}>{t('account.translations.editor_loading')}</p>}
                    {block && viewOptions.length > 0 && (
                        <div className={classes.section} data-testid="translations-editor-display">
                            <label className={classes.sectionHead}>
                                <span className={classes.label}>{t('account.translations.display_language')}</span>
                                <select
                                    className={classes.select}
                                    value={displayLanguage}
                                    onChange={event => setDisplayLanguage(event.target.value)}
                                    data-testid="translations-editor-display-language">
                                    {viewOptions.map(iso2 => (
                                        <option key={iso2} value={iso2}>
                                            {languageLabel(iso2)}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {displayValue === '' ? (
                                <div className={classes.readOnlyValue} data-testid="translations-editor-display-text">
                                    <em>{t('account.translations.missing_value')}</em>
                                </div>
                            ) : richText ? (
                                <div
                                    className={classes.readOnlyValue}
                                    data-testid="translations-editor-display-text"
                                    // the block's own formatted text, shown as it renders
                                    dangerouslySetInnerHTML={{ __html: sanitizeTextBlockHtml(displayValue) }}
                                />
                            ) : (
                                <div className={classes.readOnlyValue} data-testid="translations-editor-display-text">
                                    {displayValue}
                                </div>
                            )}
                        </div>
                    )}
                    {block && editOptions.length > 0 && (
                        <div className={classes.section} data-testid="translations-editor-edit">
                            <div className={classes.sectionHead}>
                                <span className={classes.label}>{t('account.translations.edit_language')}</span>
                                <div className={classes.pills} role="group">
                                    {editOptions.map(iso2 => (
                                        <button
                                            key={iso2}
                                            type="button"
                                            className={clsx(classes.pill, iso2 === editLanguage && 'is-active')}
                                            aria-pressed={iso2 === editLanguage}
                                            disabled={editOptions.length === 1}
                                            onClick={() => setEditLanguage(iso2)}
                                            data-testid={`translations-editor-edit-language-${iso2}`}>
                                            {languageLabel(iso2)}
                                            {isChanged(iso2) && (
                                                <span className={classes.changedMarker}>
                                                    ({t('account.translations.edited_marker')})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div ref={editorRef} data-testid={`translations-editor-${editLanguage}`}>
                                {richText ? (
                                    <SafeReactQuill
                                        key={editLanguage}
                                        theme="snow"
                                        value={drafts[editLanguage] ?? ''}
                                        onChange={setDraft}
                                        modules={RICH_TEXT_MODULES}
                                        formats={RICH_TEXT_FORMATS}
                                    />
                                ) : (
                                    <textarea
                                        key={editLanguage}
                                        className={classes.textarea}
                                        value={drafts[editLanguage] ?? ''}
                                        onChange={event => setDraft(event.target.value)}
                                        aria-label={t('account.translations.edit_language')}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    <div className={classes.actions}>
                        <button
                            type="button"
                            className={classes.ghostBtn}
                            onClick={onClose}
                            data-testid="translations-editor-close">
                            {t('account.translations.close')}
                        </button>
                        {editOptions.length > 0 && (
                            <button
                                type="button"
                                className={classes.primaryBtn}
                                disabled={saving || changedLanguages.length === 0}
                                onClick={save}
                                data-testid="translations-editor-save">
                                {t('account.translations.save')}
                            </button>
                        )}
                    </div>
                </div>
            </ModalShell>
        </ModalStackProvider>
    );
};

export default TextBlockEditModal;
